"use client";

import * as React from "react";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { PACKAGES_GRID_STYLES } from "./constants";
import { PackageCard } from "./PackageCard";
import type { PackagesGridProps } from "./types";

export const PackagesGrid = React.forwardRef<HTMLElement, PackagesGridProps>(
  ({ className, title, description, cards, onCardClick, ...props }, ref) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
      if (scrollContainerRef.current) {
        const scrollAmount = 300;
        const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
        scrollContainerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    };

    return (
      <section
        ref={ref}
        className={cn(PACKAGES_GRID_STYLES.root, className)}
        {...props}
      >
        <div className={PACKAGES_GRID_STYLES.header}>
          <div>
            <h2 className={PACKAGES_GRID_STYLES.title}>{title}</h2>
            <p className={PACKAGES_GRID_STYLES.description}>{description}</p>
          </div>
        </div>

        <div className={PACKAGES_GRID_STYLES.gridWrapper}>
          <button
            type="button"
            onClick={() => scroll('left')}
            className={cn(PACKAGES_GRID_STYLES.sideNavButton, PACKAGES_GRID_STYLES.sideNavLeft)}
            aria-label="Anterior"
          >
            <LuArrowLeft className="h-4 w-4" />
          </button>

          <div ref={scrollContainerRef} className={PACKAGES_GRID_STYLES.grid}>
            {cards.map((card) => (
              <PackageCard
                key={card.id}
                {...card}
                onClick={(event) => {
                  if (card.analyticsData && card.analyticsUrl) {
                    try {
                      const payload = btoa(unescape(encodeURIComponent(JSON.stringify(card.analyticsData))));
                      navigator.sendBeacon(card.analyticsUrl, payload);
                    } catch {
                      // Ignore analytics failures to avoid impacting UX
                    }
                  }
                  onCardClick?.(card, event);
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => scroll('right')}
            className={cn(PACKAGES_GRID_STYLES.sideNavButton, PACKAGES_GRID_STYLES.sideNavRight)}
            aria-label="Siguiente"
          >
            <LuArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    );
  }
);

PackagesGrid.displayName = "PackagesGrid";
